import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App } from '@capacitor/app';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from './downloadLinks';

const DEFAULT_MESSAGE =
  'A new version of Coach EMS is available with improvements and fixes. Update now for the best experience.';
const DEFAULT_FORCE_MESSAGE =
  'This version of Coach EMS is no longer supported. Please update to continue.';
const IOS_APP_STORE_ID = '6754012875';

/** Compare two dotted version strings. Returns 1 if a>b, -1 if a<b, 0 if equal. */
export function compareVersions(a, b) {
  const pa = String(a || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x > y) return 1;
    if (x < y) return -1;
  }
  return 0;
}

async function fetchVersionConfig() {
  // Served from the web root (deploys with the app); bump per store release.
  try {
    const res = await fetch('/app-version.json?_=' + Date.now(), { cache: 'no-store' });
    if (res.ok) return await res.json();
  } catch (e) {
    /* ignore */
  }
  return {};
}

// Auto-detect the latest published iOS version (and store URL) from the App Store.
// Uses CapacitorHttp so it isn't blocked by CORS inside the web view.
async function fetchItunesLatest(appStoreId) {
  try {
    const url = 'https://itunes.apple.com/lookup?id=' + appStoreId + '&_=' + Date.now();
    const resp = await CapacitorHttp.get({ url, headers: { Accept: 'application/json' } });
    const data = typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data;
    if (data && data.resultCount > 0 && data.results[0]) {
      return {
        version: data.results[0].version || null,
        storeUrl: data.results[0].trackViewUrl || null,
      };
    }
  } catch (e) {
    /* ignore — fall back to config */
  }
  return { version: null, storeUrl: null };
}

/**
 * Returns update info when an update is available, otherwise null.
 * { updateAvailable, forced, currentVersion, latestVersion, storeUrl, message }
 */
export async function checkForUpdate() {
  if (!Capacitor.isNativePlatform || !Capacitor.isNativePlatform()) return null;

  const platform = Capacitor.getPlatform();
  let currentVersion = '0.0.0';
  try {
    const info = await App.getInfo();
    currentVersion = info.version || '0.0.0';
  } catch (e) {
    return null;
  }

  const cfg = await fetchVersionConfig();
  let latestVersion = null;
  let minimumVersion = null;
  let storeUrl = null;

  if (platform === 'ios') {
    storeUrl = APP_STORE_URL;
    minimumVersion = cfg.ios && cfg.ios.minimumVersion;
    latestVersion = cfg.ios && cfg.ios.latestVersion;
    const appStoreId = (cfg.ios && cfg.ios.appStoreId) || IOS_APP_STORE_ID;
    const itunes = await fetchItunesLatest(appStoreId);
    if (itunes.version && (!latestVersion || compareVersions(itunes.version, latestVersion) > 0)) {
      latestVersion = itunes.version;
    }
    if (itunes.storeUrl) storeUrl = itunes.storeUrl;
  } else if (platform === 'android') {
    storeUrl = GOOGLE_PLAY_URL;
    minimumVersion = cfg.android && cfg.android.minimumVersion;
    latestVersion = cfg.android && cfg.android.latestVersion;
  } else {
    return null;
  }

  if (!latestVersion) return null; // unknown latest — don't nag

  const updateAvailable = compareVersions(currentVersion, latestVersion) < 0;
  const forced = minimumVersion ? compareVersions(currentVersion, minimumVersion) < 0 : false;
  if (!updateAvailable && !forced) return null;

  return {
    updateAvailable: true,
    forced,
    currentVersion,
    latestVersion,
    storeUrl,
    message: forced
      ? (cfg.forceUpdateMessage || DEFAULT_FORCE_MESSAGE)
      : (cfg.updateMessage || DEFAULT_MESSAGE),
  };
}

/** Open the store listing (App Store / Play Store) externally. */
export function openStore(url) {
  const target = url || (Capacitor.getPlatform() === 'ios' ? APP_STORE_URL : GOOGLE_PLAY_URL);
  try {
    window.open(target, '_system');
  } catch (e) {
    window.location.href = target;
  }
}
